import type { PageViewEvent } from '@publicala/delfino';

const DEFAULT_PAGE_VIEW_DELAY_MS = 3000;

interface EventBus {
    $on(event: string, callback: (...args: unknown[]) => void): void;
    $off(event: string, callback: (...args: unknown[]) => void): void;
    $emit(event: string, ...args: unknown[]): void;
}

type GetVisiblePages = () => number[];

/**
 * Tracks page views for fixed-page content (PDF, EPUB fixed layout).
 *
 * Unlike PagebreakTracker (which detects DOM pagebreak elements in reflowable EPUBs),
 * this tracker infers page views from page-change events: if no new page-change
 * arrives within the configured delay, the current pages are considered "viewed".
 *
 * In double-page/spread mode, emits one pageView per visible page.
 */
class FixedPageViewTracker {
    private timer: ReturnType<typeof setTimeout> | null = null;
    private eventBus: EventBus | null = null;
    private getVisiblePages: GetVisiblePages | null = null;
    private delayMs = DEFAULT_PAGE_VIEW_DELAY_MS;
    private isInitialized = false;
    private readonly handlePageChanged = () => {
        this.schedulePageViews();
    };

    /**
     * @param getVisiblePages - Returns currently visible page numbers (e.g., [5] or [5, 6] in double mode)
     * @param delayMs - Milliseconds before confirming a page view. Defaults to 3000.
     */
    init(eventBus: EventBus, getVisiblePages: GetVisiblePages, delayMs?: number | null): void {
        if (this.isInitialized) return;

        this.eventBus = eventBus;
        this.getVisiblePages = getVisiblePages;
        this.delayMs = delayMs ?? DEFAULT_PAGE_VIEW_DELAY_MS;
        this.isInitialized = true;

        this.eventBus.$on('pageChanged', this.handlePageChanged);
    }

    private schedulePageViews(): void {
        this.clearTimer();
        this.timer = setTimeout(() => {
            this.emitPageViews();
        }, this.delayMs);
    }

    private emitPageViews(): void {
        if (!this.eventBus || !this.getVisiblePages) return;

        const pages = this.getVisiblePages();
        for (const page of pages) {
            this.eventBus.$emit('pageView', {
                title: String(page),
                id: '',
            } satisfies PageViewEvent);
        }
    }

    private clearTimer(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    destroy(): void {
        this.clearTimer();

        if (this.eventBus) {
            this.eventBus.$off('pageChanged', this.handlePageChanged);
        }

        this.eventBus = null;
        this.getVisiblePages = null;
        this.isInitialized = false;
    }
}

export const fixedPageViewTracker = new FixedPageViewTracker();
